import { useEffect, useRef } from 'react'

export type CJotFormEmbedBlock = {
  typeHandle: 'jotFormEmbed'
  heading?: string
  introCopy?: string
  formId?: string
}

type Props = {
  field: Array<CJotFormEmbedBlock>
}

declare global {
  interface Window {
    jotformEmbedHandler?: (selector: string, baseUrl: string) => void
  }
}

export const CJotFormEmbed = ({ field }: Props) => {
  const block = field?.[0]
  const iframeRef = useRef<HTMLIFrameElement | null>(null)

  const formId = block?.formId?.trim()

  useEffect(() => {
    if (!formId) return

    let isUnmounted = false
    const selector = `iframe[id='JotFormIFrame-${formId}']`

    const applyIframeAttrs = () => {
      if (!iframeRef.current) return
      iframeRef.current.setAttribute('scrolling', 'no')
    }

    const runHandler = () => {
      if (isUnmounted) return

      applyIframeAttrs()

      try {
        if (typeof window.jotformEmbedHandler === 'function') {
          window.jotformEmbedHandler(selector, 'https://form.jotform.com/')
        }
      } catch (error) {
        console.error('JotForm embed handler failed', { formId, error })
      }
    }

    const existingScript = document.querySelector(
      'script[data-jotform-embed-handler="true"]'
    ) as HTMLScriptElement | null

    if (existingScript) {
      if (typeof window.jotformEmbedHandler === 'function') {
        runHandler()
      } else {
        existingScript.addEventListener('load', runHandler, { once: true })
      }
    } else {
      const script = document.createElement('script')
      script.src = 'https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js'
      script.async = true
      script.setAttribute('data-jotform-embed-handler', 'true')
      script.addEventListener('load', runHandler, { once: true })
      document.body.appendChild(script)
    }

    return () => {
      isUnmounted = true
    }
  }, [formId])

  if (!block || !formId) {
    return null
  }

  const src = `https://form.jotform.com/${formId}`

  return (
    <div className="c-embedded-form c-copy-block">
      <div className="o-container o-container--lg">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            {block.heading ? (
              <h2 className="text-4xl font-semibold text-gray-600">
                {block.heading}
              </h2>
            ) : null}

            {block.introCopy ? (
              <div
                className="s-wysiwyg text-p-lg"
                dangerouslySetInnerHTML={{ __html: block.introCopy }}
              />
            ) : null}
          </div>

          <div className="w-full overflow-visible">
            <iframe
              ref={iframeRef}
              id={`JotFormIFrame-${formId}`}
              title={block.heading || `JotForm ${formId}`}
              src={src}
              allow="geolocation; microphone; camera; fullscreen; payment"
              className="w-full"
              style={{
                minWidth: '100%',
                width: '100%',
                minHeight: '800px',
                border: 0,
                display: 'block',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export const CJotFormEmbedFragments = {
  customComponentsLandingPageBlock: `
    fragment customComponentsLandingPage_jotFormEmbed on customComponentsLandingPage_jotFormEmbed_BlockType {
      typeHandle
      heading
      introCopy
      formId
    }
  `,
}
